import { LogicBlockIndex } from '@morph-mapper/node-logic';
import type { Node as RFNode, Edge as RFEdge } from '@xyflow/react';
import { EdgeId, Graph, NodeId, buildNodeNumbers } from '../../types/graph';
import { getValues } from '@morph-mapper/utils';
import { GraphViewState } from '../../store/ui';

export type RFNodeData = {
  nodeId: NodeId;
  kind: LogicBlockIndex;
  treeIndex: string;
};

export type RFEdgeData = {
  edgeId: EdgeId;
  order: number;
  isHovered?: boolean;
};

export type RFGraphNode = RFNode<RFNodeData>;
export type RFGraphEdge = RFEdge<RFEdgeData>;

// Spawn at parent position so new nodes animate in from there instead of flying from (0,0).
const getSpawnPosition = (graph: Graph, view: GraphViewState, nodeId: NodeId) => {
  const outgoingEdge = getValues(graph.edges).find((e) => e.from.nodeId === nodeId);
  if (!outgoingEdge) return { x: 0, y: 0 };

  const parent = graph.nodes[outgoingEdge.to.nodeId];
  if (!parent) return { x: 0, y: 0 };

  return view.nodePositions[parent.id] ?? { x: 0, y: 0 };
};

export const toRFNodes = (
  graph: Graph,
  view: GraphViewState
): RFGraphNode[] => {
  const nodeNumbers = buildNodeNumbers(graph);
  return getValues(graph.nodes).map((n) => ({
    id: n.id,
    type: n.kind,
    position: view.nodePositions[n.id] ?? getSpawnPosition(graph, view, n.id),
    draggable: false,
    selectable: true,
    data: {
      nodeId: n.id,
      kind: n.kind,
      treeIndex: nodeNumbers[n.id] ?? '',
    },
  }));
};

export const toRFEdges = (graph: Graph): RFGraphEdge[] => {
  return getValues(graph.edges).map((e) => ({
    id: e.id,
    source: e.from.nodeId,
    target: e.to.nodeId,
    sourceHandle: e.from.portId,
    targetHandle: e.to.portId,
    type: 'default',
    data: {
      edgeId: e.id,
      order: e.order,
    },
  }));
};
